import { createClient } from '@/utils/supabase/component';

/** How long the dictation buttons record before stopping on their own. */
export const MAX_DICTATION_SECONDS = 600;

// Vercel rejects request bodies above ~4.5 MB, so a bigger clip is uploaded to
// the public `audios` bucket and /api/transcribe reads it by URL. The route
// deletes it afterwards, but only when the path looks like
// `<folder>/transcription-<ms>.<ext>`, so keep that name.
const DIRECT_UPLOAD_MAX_BYTES = 4 * 1024 * 1024;

const EXTENSIONS: Record<string, string> = {
  'audio/wav': 'wav',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'mp4',
  'audio/m4a': 'm4a',
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
};

// MediaRecorder reports types like `audio/webm;codecs=opus`.
const fileExtension = (mimeType: string) =>
  EXTENSIONS[mimeType.split(';')[0].trim().toLowerCase()] || 'wav';

const postTranscription = async (formData: FormData): Promise<string> => {
  const response = await fetch('/api/transcribe', {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    throw new Error(`Transcription failed (${response.status})`);
  }
  const data = await response.json();
  if (typeof data?.transcription !== 'string') {
    throw new Error('Transcription failed');
  }
  return data.transcription;
};

export interface DictationResult {
  text: string;
  sizeInMB: number;
}

/** Turns a recording from a dictation button into text. Throws on failure. */
export async function transcribeDictation(
  audio: Blob,
  { userId, language = '' }: { userId?: string | null; language?: string },
): Promise<DictationResult> {
  const sizeInMB = audio.size / (1024 * 1024);
  const formData = new FormData();
  formData.append('language', language);

  if (audio.size <= DIRECT_UPLOAD_MAX_BYTES) {
    formData.append('audio', audio);
    return { text: await postTranscription(formData), sizeInMB };
  }

  const supabase = createClient();
  const path = `${userId || 'guest'}/transcription-${Date.now()}.${fileExtension(audio.type)}`;
  const { error } = await supabase.storage
    .from('audios')
    .upload(path, audio, { contentType: audio.type || undefined });
  if (error) throw error;

  try {
    const {
      data: { publicUrl },
    } = supabase.storage.from('audios').getPublicUrl(path);
    formData.append('audioUrl', publicUrl);
    return { text: await postTranscription(formData), sizeInMB };
  } finally {
    // The route removes the clip too; this covers requests that never got
    // that far (dictations are often clinical notes on a public URL).
    await supabase.storage
      .from('audios')
      .remove([path])
      .catch(() => undefined);
  }
}
